import {
    Features,
    getFeatureFlagValue,
} from '@experiences/feature-flags';
import type { IUserLicenseAllocation } from '@experiences/interfaces';
import {
    UiProgressButton,
    UiText,
} from '@experiences/ui-common';
import {
    getDisplayName,
    getSortedUserBundleConfigurations,
    useModalState,
    useNavigateWithParams,
    useRouteResolver,
} from '@experiences/util';
import Button from '@mui/material/Button';
import { duration } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import {
    FormProvider,
    useForm,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { NavigationType } from 'react-router';
import {
    useLocation,
    useNavigationType,
} from 'react-router';
import useSWR, { useSWRConfig } from 'swr';

import { notificationType } from '../../../common/constants/Constant';
import * as RouteNames from '../../../common/constants/RouteNames';
import { useOrchRoleAssignmentForAX } from '../../../common/hooks/useOrchRoleAssignmentForAX';
import { useUiSnackBar } from '../../../common/hooks/useUiSnackBar';
import { userUrl } from '../../../services/identity/UserService';
import type { ISubmitUsersAllocations } from '../../../services/licensing/accountant/UserLicenseService';
import {
    deleteUserExplicitAllocations,
    getAccountLicenseAllocation,
    getEditableUserLicense,
    putUserLicenseAllocation,
    userLicenseUrl,
} from '../../../services/licensing/accountant/UserLicenseService';
import { trialServicesSwrKey } from '../../../services/licensing/TrialPerSku';
import { EnableUserLicensingSelector } from '../../../store/selectors';
import { UiDrawer } from '../../common/UiDrawer';
import UiForm from '../../common/UiForm';
import { useMigrationStateRevalidator } from '../flexToUniMigration/useMigrationStateRevalidator';
import type { IUserLicenseExplicitAllocation } from '../interfaces/license';
import { LicenseAllocationMethod } from '../interfaces/license';
import { LicensingAllocationFormComponent } from './LicensingAllocationFormComponent';
import { sendPendoTrackEventForAKit } from './LicensingHelpers';

const useStyles = makeStyles(theme =>
    createStyles({
        actions: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
        },
        body: { margin: '0px 24px' },
        selectLicenses: {
            fontSize: '14px',
            color: theme.palette.semantic.colorForegroundDeEmp,
            fontWeight: 600,
            marginTop: '28px',
            marginBottom: '2px',
        },
        cancelButton: { marginRight: '10px' },
        warningText: {
            color: theme.palette.error.main,
            fontSize: '14px',
            marginBottom: '16px',
            fontWeight: 600,
        },
    }),
);

const EditUserAllocationComponent: React.FC = () => {
    const EnableBulkAllocationUsersAndGroups = getFeatureFlagValue(Features.EnableBulkAllocationUsersAndGroups.name);
    const classes = useStyles();
    const navigate = useNavigateWithParams();
    const navType: NavigationType = useNavigationType();
    const { formatMessage: translate } = useIntl();
    const { createNotification } = useUiSnackBar();
    const getRoute = useRouteResolver();
    const { state } = useLocation();
    const {
        showUseOrchRoleAssignmentDialogForAX, isLoadingTenantOrchRoleInfo,
    } = useOrchRoleAssignmentForAX();

    const EnableUserLicensing = useSelector(EnableUserLicensingSelector);

    const {
        user, users, isBulkEdit, previousLocation,
    } = state;

    const isMultipleUsers = isBulkEdit && users && users.length > 0;
    const isSingleUser = !isMultipleUsers && user;

    const drawerTitle = useMemo(() => {
        if (isMultipleUsers) {
            return translate({ id: 'CLIENT_EDIT_BULK_LICENSE_ALLOCATIONS' }, { count: users.length });
        } else if (isSingleUser) {
            return translate({ id: 'CLIENT_EDIT_EXPLICIT_ALLOCATIONS_FOR_USER' }, { 0: getDisplayName(user) });
        }
        return translate({ id: 'CLIENT_EDIT_LICENSE_ALLOCATIONS' });
    }, [ isMultipleUsers, isSingleUser, users?.length, user, translate ]);

    const {
        open, close,
    } = useModalState(previousLocation ?? getRoute(RouteNames.Licensing));

    const [ loading, setLoading ] = useState(false);

    const methods = useForm<IUserLicenseExplicitAllocation>({
        mode: 'onSubmit',
        defaultValues: {
            licenseAllocationMethod: isMultipleUsers
                ? LicenseAllocationMethod.GROUP
                : LicenseAllocationMethod.EXPLICIT,
            userBundleLicenses: [],
            useExternalLicense: false,
        },
    });

    const {
        handleSubmit, reset, formState: { isDirty }, watch,
    } = methods;

    const watchedValues = watch([ 'userBundleLicenses', 'licenseAllocationMethod' ]);

    const { mutate } = useSWRConfig();
    const revalidateMigrationState = useMigrationStateRevalidator();

    const {
        data: availableUserLicenses, isValidating: loadingAvailableUserLicenses,
    } = useSWR(
        isSingleUser ? {
            url: userLicenseUrl,
            userGlobalId: user.id,
        } : null,
        getEditableUserLicense,
    );

    const {
        data: organizationLicenses, isValidating: loadingOrgLicenses,
    } = useSWR(
        isMultipleUsers ? userLicenseUrl : null,
        getAccountLicenseAllocation,
    );

    const availableBulkBundles = useMemo(() => {
        if (!isMultipleUsers || !organizationLicenses) {
            return null;
        }

        const userLicenseCounts = new Map<string, number>();
        users.forEach((user: any) => {
            if (user.userBundleLicenses && Array.isArray(user.userBundleLicenses)) {
                user.userBundleLicenses.forEach((licenseCode: string) => {
                    userLicenseCounts.set(
                        licenseCode,
                        (userLicenseCounts.get(licenseCode) || 0) + 1
                    );
                });
            }
        });

        return organizationLicenses
            .filter((license: IUserLicenseAllocation) => license.allocated !== undefined && license.total !== undefined)
            .filter((license: IUserLicenseAllocation) => license.allocated !== 0 || license.total !== 0)
            .map((license: IUserLicenseAllocation) => {
                const usersWithThisLicense = userLicenseCounts.get(license.code) || 0;
                const enhancedAllocated = Math.max(0, (license.allocated || 0) - usersWithThisLicense);

                return {
                    code: license.code,
                    allocated: enhancedAllocated,
                    total: license.total,
                    inUse: license.inUse || 0,
                    usersWithLicense: usersWithThisLicense,
                    unlimited: license.unlimited,
                };
            });
    }, [ isMultipleUsers, organizationLicenses, users ]);

    const validUserBundleCodes = useMemo(() => getSortedUserBundleConfigurations().map(s => s.code), []);

    const [ allocatedUserBundles, availableUserBundles, useExternalLicense, allocationType, licenseInheritance, hasFilteredLicenses ] =
        useMemo(() => {
            if (isMultipleUsers) {
                return [
                    [],
                    availableBulkBundles || [],
                    false,
                    LicenseAllocationMethod.EXPLICIT,
                    [],
                    false,
                ];
            }
            const userUnavailableLicenses = user?.userLicenseBundlesUnavailable || [];
            const originalAllocatedBundles = availableUserLicenses?.allocatedUserBundles || [];

            const filteredAvailableBundles = availableUserLicenses?.availableUserBundles
                ?.filter(p => p.allocated !== 0 || p.total !== 0)
                .filter(p => validUserBundleCodes.includes(p.code))
                .filter(p => EnableBulkAllocationUsersAndGroups ? !userUnavailableLicenses.includes(p.code) : true);
            const filteredAllocatedBundles = availableUserLicenses?.allocatedUserBundles
                ?.filter(licenseCode => EnableBulkAllocationUsersAndGroups ? !userUnavailableLicenses.includes(licenseCode) : true);

            const licensesWereFiltered = EnableBulkAllocationUsersAndGroups &&
                userUnavailableLicenses.length > 0 &&
                originalAllocatedBundles.some(license => userUnavailableLicenses.includes(license));

            return [
                filteredAllocatedBundles,
                filteredAvailableBundles,
                availableUserLicenses?.useExternalLicense ?? false,
                availableUserLicenses?.explicitAllocation
                    ? LicenseAllocationMethod.EXPLICIT
                    : LicenseAllocationMethod.GROUP,
                availableUserLicenses?.groupLicenseInheritance,
                licensesWereFiltered,
            ];

        }, [ isMultipleUsers, availableUserLicenses, availableBulkBundles, validUserBundleCodes, user?.userLicenseBundlesUnavailable, EnableBulkAllocationUsersAndGroups ]);

    const hasInsufficientLicenses = useMemo(() => {
        if (!isMultipleUsers || !users || !availableBulkBundles) {
            return false;
        }

        const selectedLicenses = watchedValues[0] || [];
        const allocationMethod = watchedValues[1];

        if (allocationMethod !== LicenseAllocationMethod.EXPLICIT) {
            return false;
        }

        const userCount = users.length;

        for (const licenseCode of selectedLicenses) {
            const licenseInfo = availableBulkBundles.find(bundle => bundle.code === licenseCode);
            if (licenseInfo) {
                const availableCount = (licenseInfo.total || 0) - (licenseInfo.allocated || 0);
                if (availableCount < userCount) {
                    return true;
                }
            }
        }

        return false;
    }, [ isMultipleUsers, users, availableBulkBundles, watchedValues ]);

    useEffect(() => {
        if (!EnableUserLicensing || (!user && !isMultipleUsers)) {
            console.warn('Feature not enabled or missing data. Closing...');
            navigate('/invalidurl');
        }
    }, [ EnableUserLicensing, navigate, user, isMultipleUsers ]);

    useEffect(() => {
        if (isMultipleUsers) {
            reset({
                userBundleLicenses: [],
                useExternalLicense: false,
                licenseAllocationMethod: LicenseAllocationMethod.GROUP,
            });
        } else if (allocatedUserBundles) {
            reset({
                userBundleLicenses: allocatedUserBundles,
                useExternalLicense,
                licenseAllocationMethod: allocationType,
            });
        }
    }, [ isMultipleUsers, allocatedUserBundles, useExternalLicense, allocationType, reset ]);

    const onSubmit = useCallback(
        async (data: IUserLicenseExplicitAllocation) => {
            setLoading(true);

            try {
                if (isMultipleUsers) {
                    createNotification(
                        translate(
                            { id: 'CLIENT_PROCESSING_BULK_ALLOCATIONS' },
                            { count: users.length }
                        ),
                        notificationType.DEFAULT
                    );

                    const results = {
                        success: 0,
                        failed: 0,
                        errors: [] as string[],
                    };

                    for (const user of users) {
                        try {
                            if (data.licenseAllocationMethod === LicenseAllocationMethod.GROUP) {
                                await deleteUserExplicitAllocations([ user.id ]);
                            } else if (data.licenseAllocationMethod === LicenseAllocationMethod.NONE) {
                                const payload: ISubmitUsersAllocations = {
                                    userIds: [ user.id ],
                                    licenseCodes: [],
                                    useExternalLicense: false,
                                };
                                await putUserLicenseAllocation(payload);
                            } else {
                                const payload: ISubmitUsersAllocations = {
                                    userIds: [ user.id ],
                                    licenseCodes: data.useExternalLicense ? [] : data.userBundleLicenses,
                                    useExternalLicense: data.useExternalLicense,
                                };
                                await putUserLicenseAllocation(payload);
                            }
                            results.success++;
                        } catch (userError) {
                            console.error(`Failed to update licenses for user ${getDisplayName(user)}:`, userError);
                            results.failed++;
                            results.errors.push(`${getDisplayName(user)}: ${userError}`);
                        }
                    }

                    if (results.success === users.length) {
                        createNotification(
                            translate(
                                { id: 'CLIENT_SUCCESS_EDITING_BULK_ALLOCATIONS' },
                                { count: users.length }
                            ),
                            notificationType.SUCCESS
                        );
                    } else if (results.success > 0) {
                        createNotification(
                            translate(
                                { id: 'CLIENT_PARTIAL_SUCCESS_BULK_ALLOCATIONS' },
                                {
                                    success: results.success,
                                    total: users.length,
                                }
                            ),
                            notificationType.ERROR
                        );
                    } else {
                        createNotification(
                            translate({ id: 'CLIENT_ERROR_BULK_ALLOCATIONS' }),
                            notificationType.ERROR
                        );
                    }

                } else {
                    if (data.licenseAllocationMethod === LicenseAllocationMethod.GROUP) {
                        await deleteUserExplicitAllocations([ user.id ]);
                    } else {
                        const payload: ISubmitUsersAllocations = {
                            userIds: [ user.id ],
                            licenseCodes: data.useExternalLicense ? [] : data.userBundleLicenses,
                            useExternalLicense: data.useExternalLicense,
                        };
                        await putUserLicenseAllocation(payload);
                        sendPendoTrackEventForAKit(allocatedUserBundles, data.userBundleLicenses);
                    }

                    createNotification(
                        translate(
                            { id: 'CLIENT_SUCCESS_EDITING_EXPLICIT_ALLOCATIONS' },
                            { 0: getDisplayName(user) },
                        ),
                        notificationType.SUCCESS
                    );
                }
            } catch (error) {
                console.error('License allocation error:', error);
                createNotification(
                    translate({ id: 'CLIENT_ERROR_SAVING_ALLOCATIONS' }),
                    notificationType.ERROR
                );
                setLoading(false);
                return;
            }

            await mutate(trialServicesSwrKey);
            await mutate((key: any) => key?.url === `${userUrl}/users/licenses`);
            revalidateMigrationState();
            setLoading(false);
            close(true);

            if (data.userBundleLicenses.includes('AKIT')) {
                showUseOrchRoleAssignmentDialogForAX();
            }
        },
        [
            isMultipleUsers,
            users,
            user,
            close,
            createNotification,
            translate,
            allocatedUserBundles,
            mutate,
            revalidateMigrationState,
            showUseOrchRoleAssignmentDialogForAX,
        ],
    );

    return (
        <UiDrawer
            title={drawerTitle}
            drawerProps={{
                anchor: 'right',
                open,
                onClose: () => close(),
                transitionDuration: navType !== 'POP' ? duration.enteringScreen : 0,
            }}
            themeProps={{ disableGutters: true }}
            loading={isLoadingTenantOrchRoleInfo || loadingAvailableUserLicenses || loadingOrgLicenses}
        >
            <FormProvider {...methods}>
                <UiForm
                    onSubmit={handleSubmit(onSubmit)}
                    actions={
                        <>
                            <div className={classes.actions}>
                                <Button
                                    className={classes.cancelButton}
                                    onClick={() => close()}
                                    color="primary">
                                    {translate({ id: 'CLIENT_CANCEL' })}
                                </Button>
                                <UiProgressButton
                                    loading={loading}
                                    disabled={
                                        isMultipleUsers
                                            ? hasInsufficientLicenses
                                            : !isDirty && !hasFilteredLicenses
                                    }
                                    onClick={handleSubmit(onSubmit)}
                                    variant="contained"
                                    data-cy="save-submit-button"
                                >
                                    {translate({ id: 'CLIENT_SAVE' })}
                                </UiProgressButton>
                            </div>
                        </>
                    }
                    isDrawer
                >
                    <UiText className={classes.selectLicenses}>
                        {translate({ id: 'CLIENT_USER_LICENSE_ALLOCATION' })}
                    </UiText>
                    {isMultipleUsers && hasInsufficientLicenses && (
                        <UiText
                            className={classes.warningText}
                            data-cy="insufficient-licenses-warning">
                            ⚠️
                            {' '}
                            {translate({ id: 'CLIENT_INSUFFICIENT_LICENSES_WARNING' }, { count: users.length })}
                        </UiText>
                    )}
                    <LicensingAllocationFormComponent
                        availableUserBundles={availableUserBundles}
                        allocatedUserBundles={allocatedUserBundles}
                        groupLicenseInheritance={licenseInheritance}
                        userId={isMultipleUsers ? null : user?.id}
                        withRadioGroup
                    />
                </UiForm>
            </FormProvider>
        </UiDrawer>
    );
};

export default EditUserAllocationComponent;
